import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star } from "lucide-react";
import { GALLERY } from "@/lib/data";
import { Reveal, Overline } from "@/components/Reveal";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const SPAN_PATTERN = ["tall", "", "", "wide", "", "tall", "", ""];

const spanClass = (span) => {
  if (span === "tall") return "row-span-2";
  if (span === "wide") return "sm:col-span-2";
  return "";
};

export default function Gallery() {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState("All");

  useEffect(() => {
    api
      .get("/gallery")
      .then((r) => {
        if (r.data.length > 0) {
          setItems(
            r.data.map((d, i) => ({
              src: `${BACKEND}${d.url}`,
              label: d.caption || "Temple Bells",
              album: d.album || "Wedding",
              featured: !!d.featured,
              span: SPAN_PATTERN[i % SPAN_PATTERN.length],
            }))
          );
        } else {
          setItems(GALLERY);
        }
      })
      .catch(() => setItems(GALLERY));
  }, []);

  const data = items || GALLERY;
  const featured = useMemo(() => data.filter((d) => d.featured), [data]);
  const albums = useMemo(() => ["All", ...Array.from(new Set(data.map((d) => d.album)))], [data]);
  const grid = useMemo(
    () => (active === "All" ? data.filter((d) => !d.featured) : data.filter((d) => d.album === active)),
    [data, active]
  );

  return (
    <section
      id="gallery"
      className="relative py-24 sm:py-32 lg:py-40 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16"
      data-testid="gallery-section"
    >
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-5 mb-12">
        <div>
          <Reveal>
            <Overline testid="gallery-overline">Portfolio</Overline>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight">
              Moments we've <span className="italic text-[var(--gold)]">framed</span>.
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <p className="max-w-xs text-sm text-[var(--ink-2)] font-light">
            A glimpse into the celebrations we've designed — from intimate rituals to grand affairs.
          </p>
        </Reveal>
      </div>

      {/* Featured highlights */}
      {featured.length > 0 && (
        <div className="mb-14" data-testid="gallery-featured">
          <Reveal>
            <div className="flex items-center gap-2 mb-5">
              <Star size={14} className="text-[var(--gold)]" fill="currentColor" strokeWidth={0} />
              <span className="text-[11px] uppercase tracking-[0.3em] text-[var(--ink-3)]">
                Featured Highlights
              </span>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-3 sm:gap-4">
            {featured.map((g, i) => (
              <motion.figure
                key={`feat-${i}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className={`group relative overflow-hidden rounded-[3px] aspect-[16/10] ${
                  featured.length % 2 !== 0 && i === 0 ? "sm:col-span-2 sm:aspect-[21/9]" : ""
                }`}
              >
                <img
                  src={g.src}
                  alt={g.label}
                  style={{ transitionDuration: "1200ms" }}
                  className="w-full h-full object-cover transition-transform ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/50 via-transparent to-transparent" />
                <figcaption className="absolute bottom-4 left-4 flex items-center gap-2">
                  <span className="font-accent text-[10px] tracking-[0.3em] text-white/70">
                    {g.album.toUpperCase()}
                  </span>
                  <span className="font-serif text-xl text-white">{g.label}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      )}

      {/* Album filter */}
      {albums.length > 2 && (
        <div className="flex flex-wrap gap-2 mb-8" data-testid="gallery-album-filter">
          {albums.map((a) => (
            <button
              key={a}
              onClick={() => setActive(a)}
              className={`relative text-sm px-5 py-2 rounded-full border transition-colors duration-300 ${
                active === a
                  ? "bg-[var(--ink)] text-[var(--bg)] border-[var(--ink)]"
                  : "border-[var(--border-gold)] text-[var(--ink-2)] hover:border-[var(--gold)] hover:text-[var(--ink)]"
              }`}
              data-testid={`album-tab-${a}`}
            >
              {a}
            </button>
          ))}
        </div>
      )}

      {/* Bento grid */}
      <motion.div
        layout
        className="grid grid-cols-2 sm:grid-cols-3 auto-rows-[200px] sm:auto-rows-[260px] gap-3 sm:gap-4"
      >
        <AnimatePresence mode="popLayout">
          {grid.map((g, i) => (
            <motion.figure
              layout
              key={`${active}-${g.src}-${i}`}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden rounded-[2px] ${spanClass(g.span)}`}
              data-testid={`gallery-item-${i}`}
            >
              <img
                src={g.src}
                alt={g.label}
                style={{ transitionDuration: "1200ms" }}
                className="w-full h-full object-cover transition-transform ease-out group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-[var(--ink)]/0 group-hover:bg-[var(--ink)]/30 transition-colors duration-500" />
              <figcaption className="absolute bottom-3 left-3 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                <span className="font-accent text-[10px] tracking-[0.3em] text-white">
                  {g.label.toUpperCase()}
                </span>
              </figcaption>
            </motion.figure>
          ))}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
